import type { AxiosRequestConfig } from 'axios';
import { safelyParseJson } from '@olivierpascal/helpers';
import axios from 'axios';

import { ApiError } from './ApiError';

export type IJsonRequest<TBody extends object = object> = Omit<
  AxiosRequestConfig,
  'responseType' | 'data'
> & {
  expectedKey?: string;
  body?: TBody;
};

export type IResponse<TBody> = {
  statusCode: number;
  body: TBody;
  context?: object;
};

export const fetchJson = async <TResponseBody = unknown>(
  jsonRequest: IJsonRequest,
): Promise<IResponse<TResponseBody>> => {
  const request: AxiosRequestConfig = {
    ...jsonRequest,
    headers: {
      accept: 'application/json, text/javascript, */*; q=0.01',
      'content-type': 'application/json',
      ...jsonRequest.headers,
    },
    data: JSON.stringify(jsonRequest.body),
    responseType: 'text',
  };
  const response = await axios.request(request);

  const responseBody = safelyParseJson<TResponseBody>(response.data as string);
  if (!responseBody) {
    const message = `Malformed fetch response object: ${JSON.stringify(response.data)}`;

    // Error can be reported.

    throw new Error(message);
  }

  if (response.status >= 400) {
    const errorResponseBody = responseBody
      ? (responseBody as unknown as { message: string })
      : undefined;

    throw new ApiError(
      [
        {
          code: errorResponseBody?.message ?? `HTTP_ERROR_${response.status}`,
          type: 'HttpError',
          message:
            errorResponseBody?.message ??
            'An error occurred while processing your request. Please try again later.',
          timestamp: new Date().toISOString(),
          details: response.data,
        },
      ],
      {
        request,
        response: {
          status: response.status,
          data: response.data,
        },
      },
    );
  }

  if (
    jsonRequest.expectedKey &&
    !Object.prototype.hasOwnProperty.call(responseBody, jsonRequest.expectedKey)
  ) {
    const message = `Invalid fetch response object. Expected a JSON object with key \`${String(
      jsonRequest.expectedKey,
    )}\`.`;

    // Error can be reported.

    throw new Error(message);
  }

  return {
    statusCode: response.status,
    body: responseBody,
    context: {
      request,
      response: {
        status: response.status,
        data: response.data,
      },
    },
  };
};
